import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { ITcProductCertifiedWeightInformation } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { useFormActions } from "../../../../../../../shared/hooks/form-builder.hooks";
import useNotification from "../../../../../../../shared/hooks/useNotification";
import { Utills } from "../../../../../../../shared/app-configs/utilit";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";
import { useParams } from "react-router-dom";
import { LoaderComponent } from "../../../../../../../shared/components/loaders/page-loader";

interface ProductCertifiedWeightProps {
  formRef: React.MutableRefObject<any>;
  productCertifiedWeightInfoData: ITcProductCertifiedWeightInformation[];
  setProductCertifiedWeightInfoData: React.Dispatch<
    React.SetStateAction<ITcProductCertifiedWeightInformation[]>
  >;
  productData: any;
  rawMaterialInfoData: any[];
}

/**
 * Step 3 — Product Certified Weight Information.
 * Per the wireframe this is a READ-ONLY table grouped by claims category: it takes the CERTIFIED raw
 * materials from Step 2 (Raw Material Info), uses each row's user-entered claims category and its
 * auto-calculated weight (= (net - supplementary - additives) * RM% / 100), groups by claims category
 * and sums the weights. No add/edit/delete. The derived rows are pushed up so the submit payload uses
 * exactly this grouped certified-weight information.
 */
const ProductCertifiedWeight: React.FC<ProductCertifiedWeightProps> = ({
  formRef,
  setProductCertifiedWeightInfoData,
  productData,
  rawMaterialInfoData,
}) => {
  const { notify } = useNotification();
  const { updateField } = useFormActions("certified-products");
  const { TE_ID } = useParams();
  const { executeGet } = useApi();

  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<any[]>([]);
  const [tcStandardList, setTcStandardList] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const lastPushed = useRef<string>("");

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const [certResp, baseResp] = await Promise.all([
          executeGet(TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES),
          executeGet(TCAppConfig.GET_TC_BASE_DETAILS_V4, { tcId: TE_ID }),
        ]);
        const certList = certResp?.data?.response || certResp?.data;
        setCertifiedRawMaterialCodes(Array.isArray(certList) ? certList : []);
        const standards = baseResp?.data?.tcStandard;
        setTcStandardList(Array.isArray(standards) ? standards : []);
      } catch (err) {
        Utills.cl(`Error initializing certified weight step: ${err}`);
        notify("Failed to load certified weight information", "error");
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  const isCertifiedRM = (code: string): boolean => {
    if (!code || !Array.isArray(certifiedRawMaterialCodes) || certifiedRawMaterialCodes.length === 0)
      return false;
    const mapping = certifiedRawMaterialCodes.find((item: any) => item.material_code === code);
    const rawStandards: string = mapping?.standard || "";
    if (!rawStandards || rawStandards.toUpperCase() === "NA") return false;
    const standards = rawStandards
      .split("/")
      .map((s: string) => s.trim())
      .filter((s: string) => s && s.toUpperCase() !== "NA");
    if (!standards.length || !Array.isArray(tcStandardList) || tcStandardList.length === 0)
      return false;
    const tcStandardsLower = tcStandardList.map((s) => String(s).toLowerCase());
    return standards.some((std) => tcStandardsLower.includes(std.toLowerCase()));
  };

  const getProductWeight = (): number => {
    const net = Number(productData?.tcProductNetShippingWeight ?? productData?.tcNetShippingWeight ?? 0);
    const supp = Number(productData?.tcProductSupplementaryWeight ?? 0);
    const additives = Number(productData?.tcProductAdditivesWeight ?? 0);
    return net - supp - additives;
  };

  // Derived group-by: certified RMs grouped by their claims category, weights summed.
  const grouped = useMemo<ITcProductCertifiedWeightInformation[]>(() => {
    const rows = Array.isArray(rawMaterialInfoData) ? rawMaterialInfoData : [];
    const productWeight = getProductWeight();
    const ready =
      Array.isArray(certifiedRawMaterialCodes) &&
      certifiedRawMaterialCodes.length > 0 &&
      Array.isArray(tcStandardList) &&
      tcStandardList.length > 0;
    const map = new Map<string, number>();
    rows.forEach((r: any) => {
      // Canonical: live recompute when the mapping is loaded; stored flag only as pre-load fallback.
      const certified = ready ? isCertifiedRM(r.tcProductRawMaterialCode) : r.certified === true;
      if (!certified) return;
      const claimsCategory = String(r.claimsCategory || "").trim();
      if (!claimsCategory) return;
      const weight = (productWeight * Number(r.tcProductRawMaterialPercentage || 0)) / 100;
      map.set(claimsCategory, (map.get(claimsCategory) || 0) + weight);
    });
    return Array.from(map.entries()).map(([claimsCategory, weight]) => ({
      tcProductClaimsCategory: claimsCategory,
      tcProductClaimsCategoryCertifiedWeight: Number(weight.toFixed(2)),
      tcProductCertifiedWeight: Number(weight.toFixed(2)),
      tcProductRawMaterialCodeClaimsCategory: "",
      tcProductRawMaterialCodeClaimsCategoryDescription: "",
    }));
  }, [rawMaterialInfoData, productData, certifiedRawMaterialCodes, tcStandardList]);

  const totalCertifiedWeight = useMemo(
    () => grouped.reduce((sum, r) => sum + Number(r.tcProductClaimsCategoryCertifiedWeight || 0), 0),
    [grouped]
  );

  // Push the derived grouped data up to the parent so the POST payload uses it (loop-guarded).
  useEffect(() => {
    const serialized = JSON.stringify(grouped);
    if (serialized !== lastPushed.current) {
      lastPushed.current = serialized;
      setProductCertifiedWeightInfoData(grouped);
      try {
        updateField("productCertifiedWeightInfo", grouped);
      } catch (err) {
        Utills.cl(`Error pushing certified weight info: ${err}`);
      }
    }
  }, [grouped]);

  // Read-only step: never block Next (no editing); expose matching sums for the parent's gate.
  useEffect(() => {
    if (formRef) {
      formRef.current = {
        editingRowIndex: -1,
        hasUnsavedRow: false,
        tableSum: totalCertifiedWeight,
        expectedTotal: totalCertifiedWeight,
        validateForm: async () => ({}),
      };
    }
  }, [formRef, totalCertifiedWeight]);

  return (
    <Box component="main" sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}>
      {isLoading ? (
        <LoaderComponent />
      ) : (
        <>
          <Box mb={2}>
            <Typography>
              <span style={{ fontWeight: 600 }}>Total Certified Product Weight: </span>
              {totalCertifiedWeight.toFixed(2)} kg
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Auto-derived from the certified raw materials in the Raw Material step, grouped by claims
              category. This table is read only.
            </Typography>
          </Box>
          {grouped.length === 0 ? (
            <Typography color="text.secondary" sx={{ my: 2 }}>
              No certified raw materials with a claims category yet. Add certified raw materials and
              select their claims category in the Raw Material step.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#2C4748" }}>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      Product Claims Category
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      Product Claims Category Certified Weight
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grouped.map((row, i) => (
                    <TableRow key={`${row.tcProductClaimsCategory}-${i}`}>
                      <TableCell>{row.tcProductClaimsCategory}</TableCell>
                      <TableCell>
                        {Number(row.tcProductClaimsCategoryCertifiedWeight || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default ProductCertifiedWeight;
